import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { DayBoundary } from '@/utils/dates';
import { ShippingsFilter } from '../models';
import { useQueryParams } from '@/hooks';

const SHIPPINGS_FILTERS_STORAGE_KEY = 'BUSINESS-SHIPPINGS-FILTERS';

const getShippingsFilterDefaultDateFields = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const todayEnd = new Date();
  todayEnd.setHours(23, 59, 59, 999);

  const tenDaysAgo = new Date(today);
  tenDaysAgo.setDate(today.getDate() - 10);
  tenDaysAgo.setHours(0, 0, 0, 0);

  const tenDaysAhead = new Date(today);
  tenDaysAhead.setDate(today.getDate() + 10);
  tenDaysAhead.setHours(23, 59, 59, 999);

  const fortyFiveDaysAgo = new Date(today);
  fortyFiveDaysAgo.setDate(today.getDate() - 45);
  fortyFiveDaysAgo.setHours(0, 0, 0, 0);

  return {
    deliveryDateFrom: tenDaysAgo,
    deliveryDateTo: tenDaysAhead,
    createdAtFrom: fortyFiveDaysAgo,
    createdAtTo: todayEnd,
  };
};

const SHIPPINGS_FILTER_QUERY_FIELDS = {
  string: [
    'logisticOperatorId',
    'commerceId',
    'number',
    'commerceShippingNumber',
    'labelNumber',
    'recipient',
    'recipientPhoneNumber',
    'destinationAddress',
    'driverName',
    'driverPhoneNumber',
    'originAddress',
    'branchId',
    'branch',
    'tags',
    'statusId',
    'destinationStateOrDepartmentName',
  ],
  commaList: [
    'cityIds',
    'cityNames',
    'destinationStateOrDepartmentIds',
    'timeSlotIds',
    'serviceWithOperatorIds',
    'zoneIds',
    'zoneNames',
  ],

  calendarDay: [
    'deliveryDateFrom',
    'deliveryDateTo',
    'dateDeliveredFrom',
    'dateDeliveredTo',
    'createdAtFrom',
    'createdAtTo',
  ],
} satisfies {
  string: readonly (keyof ShippingsFilter)[];
  commaList: readonly (keyof ShippingsFilter)[];
  calendarDay: readonly (keyof ShippingsFilter)[];
};

const parseIsoDateFromJson = (
  value: unknown,
  fallback: Date | null | undefined
) => {
  if (value == null || value === '') return fallback;
  const parsed = new Date(value as string);
  return Number.isNaN(parsed.getTime()) ? fallback : parsed;
};

const resolveInitialShippingsFilter = (
  emptyFilterTemplate: ShippingsFilter,
  locationSearch: string,
  readQueryParam: (key: string) => string | null
): ShippingsFilter => {
  const storageJson = localStorage.getItem(SHIPPINGS_FILTERS_STORAGE_KEY);
  let filterAfterStorage: ShippingsFilter = emptyFilterTemplate;

  if (storageJson) {
    try {
      const parsed = JSON.parse(storageJson) as Record<string, unknown>;

      filterAfterStorage = {
        ...emptyFilterTemplate,
        ...parsed,
        cityIds: Array.isArray(parsed.cityIds)
          ? (parsed.cityIds as string[])
          : emptyFilterTemplate.cityIds,

        destinationStateOrDepartmentIds: Array.isArray(
          parsed.destinationStateOrDepartmentIds
        )
          ? (parsed.destinationStateOrDepartmentIds as string[])
          : emptyFilterTemplate.destinationStateOrDepartmentIds,

        cityNames: Array.isArray(parsed.cityNames)
          ? (parsed.cityNames as string[])
          : emptyFilterTemplate.cityNames,

        timeSlotIds: Array.isArray(parsed.timeSlotIds)
          ? (parsed.timeSlotIds as string[])
          : emptyFilterTemplate.timeSlotIds,
        serviceWithOperatorIds: Array.isArray(parsed.serviceWithOperatorIds)
          ? (parsed.serviceWithOperatorIds as string[])
          : emptyFilterTemplate.serviceWithOperatorIds,
        zoneIds: Array.isArray(parsed.zoneIds)
          ? (parsed.zoneIds as string[])
          : emptyFilterTemplate.zoneIds,
        zoneNames: Array.isArray(parsed.zoneNames)
          ? (parsed.zoneNames as string[])
          : emptyFilterTemplate.zoneNames,
        deliveryDateFrom: parseIsoDateFromJson(
          parsed.deliveryDateFrom,
          emptyFilterTemplate.deliveryDateFrom
        ),
        deliveryDateTo: parseIsoDateFromJson(
          parsed.deliveryDateTo,
          emptyFilterTemplate.deliveryDateTo
        ),
        dateDeliveredFrom: parseIsoDateFromJson(
          parsed.dateDeliveredFrom,
          emptyFilterTemplate.dateDeliveredFrom
        ),
        dateDeliveredTo: parseIsoDateFromJson(
          parsed.dateDeliveredTo,
          emptyFilterTemplate.dateDeliveredTo
        ),
        createdAtFrom: parseIsoDateFromJson(
          parsed.createdAtFrom,
          emptyFilterTemplate.createdAtFrom
        ),
        createdAtTo: parseIsoDateFromJson(
          parsed.createdAtTo,
          emptyFilterTemplate.createdAtTo
        ),
        statusId:
          typeof parsed.statusId === 'string'
            ? parsed.statusId
            : emptyFilterTemplate.statusId,
      };
    } catch {
      filterAfterStorage = emptyFilterTemplate;
    }
  }

  const urlParams = new URLSearchParams(locationSearch);
  const defaultDateFields = getShippingsFilterDefaultDateFields();
  const filterAfterUrl = { ...filterAfterStorage };

  if (urlParams.has('commerceId')) {
    const commerceIdFromUrl = readQueryParam('commerceId') || '';
    if (commerceIdFromUrl !== filterAfterStorage.commerceId) {
      filterAfterUrl.branchId = '';
    }
  }

  for (const key of SHIPPINGS_FILTER_QUERY_FIELDS.string) {
    if (!urlParams.has(key)) continue;
    const fromUrl = readQueryParam(key);
    (filterAfterUrl as Record<string, unknown>)[key] = fromUrl ?? '';
  }

  for (const key of SHIPPINGS_FILTER_QUERY_FIELDS.commaList) {
    if (!urlParams.has(key)) continue;
    const fromUrl = readQueryParam(key);
    (filterAfterUrl as Record<string, unknown>)[key] = fromUrl
      ? fromUrl.split(',').filter(Boolean)
      : [];
  }

  for (const key of SHIPPINGS_FILTER_QUERY_FIELDS.calendarDay) {
    if (!urlParams.has(key)) continue;
    const fromUrl = readQueryParam(key);
    const fallbackDate =
      defaultDateFields[key as keyof typeof defaultDateFields];
    if (!fromUrl) {
      (filterAfterUrl as Record<string, unknown>)[key] = fallbackDate;
      continue;
    }
    const boundary: DayBoundary = key.endsWith('To') ? 'end' : 'start';
    const parsedDate = parseCalendarDayFromUrl(fromUrl, boundary);
    (filterAfterUrl as Record<string, unknown>)[key] =
      parsedDate ?? fallbackDate;
  }

  return filterAfterUrl;
};

const saveShippingsFilterToLocalStorage = (filter: ShippingsFilter) => {
  const jsonSafeFilter = {
    logisticOperatorId: filter.logisticOperatorId,
    commerceId: filter.commerceId,
    number: filter.number,
    commerceShippingNumber: filter.commerceShippingNumber,
    labelNumber: filter.labelNumber,
    recipient: filter.recipient,
    recipientPhoneNumber: filter.recipientPhoneNumber,
    destinationAddress: filter.destinationAddress,
    driverName: filter.driverName,
    driverPhoneNumber: filter.driverPhoneNumber,
    originAddress: filter.originAddress,
    cityIds: filter.cityIds,
    cityNames: filter.cityNames,
    destinationStateOrDepartmentIds: filter.destinationStateOrDepartmentIds,
    destinationStateOrDepartmentName: filter.destinationStateOrDepartmentName,
    timeSlotIds: filter.timeSlotIds,
    branchId: filter.branchId,
    branch: filter.branch,
    tags: filter.tags,
    serviceWithOperatorIds: filter.serviceWithOperatorIds,
    zoneIds: filter.zoneIds,
    zoneNames: filter.zoneNames,
    statusId: filter.statusId,
    deliveryDateFrom: filter.deliveryDateFrom?.toISOString() ?? null,
    deliveryDateTo: filter.deliveryDateTo?.toISOString() ?? null,
    dateDeliveredFrom: filter.dateDeliveredFrom?.toISOString() ?? null,
    dateDeliveredTo: filter.dateDeliveredTo?.toISOString() ?? null,
    createdAtFrom: filter.createdAtFrom?.toISOString() ?? null,
    createdAtTo: filter.createdAtTo?.toISOString() ?? null,
  };
  try {
    localStorage.setItem(
      SHIPPINGS_FILTERS_STORAGE_KEY,
      JSON.stringify(jsonSafeFilter)
    );
  } catch {

  }
};

const formatFilterDateForUrl = (date?: Date | null) => {
  if (!date) return undefined;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/;

const parseCalendarDayFromUrl = (
  raw: string,
  boundary: DayBoundary
): Date | null => {
  if (DATE_ONLY_REGEX.test(raw)) {
    const [year, month, day] = raw.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    if (boundary === 'end') {
      date.setHours(23, 59, 59, 999);
    }
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatFilterDateTimeForUrl = (date?: Date | null) => {
  if (!date) return undefined;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  const millis = String(date.getMilliseconds()).padStart(3, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${millis}`;
};

const buildUrlQueryFromShippingsFilter = (
  filter: ShippingsFilter,
  readQueryParam: (key: string) => string | null
) => ({
  logisticOperatorId: filter.logisticOperatorId,
  commerceId: filter.commerceId,
  number: filter.number,
  commerceShippingNumber: filter.commerceShippingNumber,
  labelNumber: filter.labelNumber,
  recipient: filter.recipient,
  recipientPhoneNumber: filter.recipientPhoneNumber,
  destinationAddress: filter.destinationAddress,
  driverName: filter.driverName,
  driverPhoneNumber: filter.driverPhoneNumber,
  originAddress: filter.originAddress,
  cityIds: filter.cityIds?.join(',') || undefined,
  cityNames: filter.cityNames?.join(',') || undefined,
  destinationStateOrDepartmentIds: filter.destinationStateOrDepartmentIds?.join(',') || undefined,
  destinationStateOrDepartmentName:
    filter.destinationStateOrDepartmentName || undefined,
  timeSlotIds: filter.timeSlotIds?.join(',') || undefined,
  branchId: filter.branchId,
  branch: filter.branch,
  tags: filter.tags,
  serviceWithOperatorIds: filter.serviceWithOperatorIds?.join(',') || undefined,
  zoneIds: filter.zoneIds?.join(',') || undefined,
  zoneNames: filter.zoneNames?.join(',') || undefined,
  statusId: filter.statusId?.trim() ? filter.statusId : undefined,
  deliveryDateFrom: formatFilterDateForUrl(filter.deliveryDateFrom),
  deliveryDateTo: formatFilterDateForUrl(filter.deliveryDateTo),
  dateDeliveredFrom: formatFilterDateForUrl(filter.dateDeliveredFrom),
  dateDeliveredTo: formatFilterDateForUrl(filter.dateDeliveredTo),
  createdAtFrom: formatFilterDateTimeForUrl(filter.createdAtFrom),
  createdAtTo: formatFilterDateTimeForUrl(filter.createdAtTo),
  page: '1',
  pageSize: readQueryParam('pageSize') || localStorage.getItem('shippings_pageSize') || '50',
});

const useShippingsFilter = () => {
  const location = useLocation();
  const { getQueryParam, setQueryParams } = useQueryParams();

  const initialFilter: ShippingsFilter = useMemo(() => {
    const defaultDateFields = getShippingsFilterDefaultDateFields();

    return {
      logisticOperatorId: '',
      commerceId: '',
      number: '',
      commerceShippingNumber: '',
      labelNumber: '',
      recipient: '',
      recipientPhoneNumber: '',
      destinationAddress: '',
      driverName: '',
      driverPhoneNumber: '',
      originAddress: '',
      cityIds: [],
      cityNames: [],
      destinationStateOrDepartmentIds: [],
      destinationStateOrDepartmentName: '',
      timeSlotIds: [],
      branchId: '',
      branch: '',
      tags: '',
      serviceWithOperatorIds: [],
      zoneIds: [],
      zoneNames: [],
      statusId: '',
      ...defaultDateFields,
    };
  }, []);

  const [filter, setFilter] = useState<ShippingsFilter>(() =>
    resolveInitialShippingsFilter(initialFilter, location.search, getQueryParam)
  );

  useEffect(() => {
    saveShippingsFilterToLocalStorage(filter);
    setQueryParams(buildUrlQueryFromShippingsFilter(filter, getQueryParam));
  }, [filter]);

  return { filter, initialFilter, setFilter };
};

export default useShippingsFilter;
